const QuickbooksTransactionsAdapter = require('../quickbooks/quickbooks-transactions-adapter')
const singleTransactionQuery = require('./queries/single-transaction-query')
const { createTransactionsListQuery } = require('./queries/transactions-list-query')
const { createUpcomingDueTransactionsQuery } = require('./queries/upcoming-due-transactions-query')
const { createPinnedTransactionsListQuery } = require('./queries/pinned-transactions-list-query')
const { paymentPlanInstalmentsQuery } = require('./queries/payment-plan-instalments-query')
const { txnTypeQuery } = require('./queries/pay-in-instalment-query')
const getOrderCreditsQuery = require('./queries/order-credits-query')
const futureInstalmentQuery = require('./queries/future-instalment-query')
const accountReceivableBalanceQuery = require('./queries/account-receivable-balance-query')
const { getPositionalArgs } = require('../tools/sql-tools')
const { TABLE_NAMES, SHIPMENT_WINDOW } = require('./constants')
const { calculateBalance, getCreditInfo } = require('./tools')
const groupBy = require('lodash/groupBy')
const reduce = require('lodash/reduce')
const { optimizedPayablesListQuery, optimizedTransactionListQuery } = require('./queries/optimized-v1-queries')

class TransactionsAdapter extends QuickbooksTransactionsAdapter {
  constructor (pgConnection, username, dataShipment, creditApplication, priceAdapter) {
    super(pgConnection, username)
    this.dataShipment = dataShipment
    this.creditApplication = creditApplication
    this.priceAdapter = priceAdapter
  }

  async getList ({
    limit = 50,
    offset = 0,
    locationId,
    txnType,
    noLimit, // in case for pdf statement we need to fetch all at once
    overduePayment = false,
    startDate,
    endDate
  }) {
    const {query, props} = optimizedTransactionListQuery({ limit, offset, locationId, txnType, noLimit, overduePayment, startDate, endDate })
    const { rows } = await this.pgConnection.query(query, props)
    return {
      results: rows
    }
  }

  async getListByLocationIds ({
    txnType,
    overduePayment = false,
    locationIds = []
  }) {
    const dollarSigns = getPositionalArgs(locationIds)
    const props = [...locationIds]
    let whereCondition = `WHERE location_id IN (${dollarSigns})`
    if (txnType) {
      props.push(txnType)
      whereCondition = `${whereCondition} AND tl.txn_type = $${props.length} `
    }
    if (overduePayment) {
      whereCondition = `${whereCondition} AND tl.amount_paid < tl.amount AND now() >= tl.due_date`
    }
    const orderCondition = 'ORDER BY txn_date DESC, txn_type'
    const transactionsListQuery = createTransactionsListQuery()
    const query = `${transactionsListQuery} ${whereCondition} ${orderCondition}`
    const { rows } = await this.pgConnection.query(query, props)
    return {
      results: rows
    }
  }

  async getUpcomingDueTransactions ({
    checkNotificationSend = false
  } = {}) {
    const transactionsListQuery = createUpcomingDueTransactionsQuery({ isNotificationSend: checkNotificationSend })
    const whereCondition = `
       WHERE tl.amount_paid < tl.amount
       AND (tl.due_date::DATE - now()::DATE) = 1
       AND (tt.txn_type = 'installment' OR tt.txn_type = 'invoice')
       ${checkNotificationSend ? 'AND "n".id IS NULL' : ''}
    `
    const query = `${transactionsListQuery} ${whereCondition}`
    const { rows } = await this.pgConnection.query(query)
    return {
      results: rows
    }
  }

  async getPinnedTransactions ({
    locationId,
    startDate,
    endDate
  }) {
    const props = [locationId]
    let whereCondition = 'WHERE "location_id" = $1'
    if (startDate && endDate) {
      whereCondition = `${whereCondition} AND txn_date >= $2 and txn_date < $3`
      props.push(...[startDate, endDate])
    }
    const orderCondition = 'ORDER BY txn_date DESC, txn_type ASC'
    const pinnedTransactionsListQuery = createPinnedTransactionsListQuery()
    const query = `${pinnedTransactionsListQuery} ${whereCondition} ${orderCondition}`
    const { rows } = await this.pgConnection.query(query, props)
    return {
      results: rows
    }
  }

  async getPayablesList ({locationId, excludeOrderId, includeTxnInfo = false, groupPayables = false}) {
    const params = excludeOrderId ? [locationId, excludeOrderId] : [locationId]
    const payablesListQuery = optimizedPayablesListQuery(excludeOrderId, includeTxnInfo)
    const { rows } = await this.pgConnection.query(payablesListQuery, params)
    if (groupPayables) {
      const payablesList = groupBy(rows, 'week')
      const data = reduce(payablesList, (result, value, key) => {
        result[key] = value[0]
        return result
      }, {})
      return data
    }
    return { results: rows }
  }

  async getPaymentPlanWithInstalments ({locationId, startDate, endDate, paymentPlanId}) {
    const props = [locationId]
    let whereCondition = 'WHERE "location_id" = $1'
    if (startDate && endDate) {
      whereCondition = `${whereCondition} AND start_date >= $2 and start_date < $3`
      props.push(...[startDate, endDate])
    }
    if (paymentPlanId) {
      whereCondition = `${whereCondition} AND payment_plan_id = $${props.length + 1}`
      props.push(paymentPlanId)
    }
    const paymentPlanInstalmentQuery = paymentPlanInstalmentsQuery()
    const query = `${paymentPlanInstalmentQuery} ${whereCondition}`
    const { rows } = await this.pgConnection.query(query, props)
    return {
      results: rows
    }
  }

  async getAccountReceivableBalance ({ locationId }) {
    const { rows } = await this.pgConnection.query(accountReceivableBalanceQuery, [locationId])
    return rows[0]
  }

  async getLoanReceivableBalance ({ locationId }) {
    const query = `SELECT -SUM(GREATEST(COALESCE("quickbooks_account_balance", 0), 0)) AS lr_balance
      FROM ${TABLE_NAMES.PAYMENT_PLAN_TABLE_NAME} "pp"
       INNER JOIN ${TABLE_NAMES.TRANSACTIONS_TABLE_NAME} "qt"
       ON "qt"."company_code" = "pp"."company_code" AND "qt"."txn_id" = "pp"."quickbooks_journal_entry_id"
      WHERE "pp"."location_id" = $1`
    const { rows } = await this.pgConnection.query(query, [locationId])
    return rows[0]
  }

  async getOne (data) {
    const {
      transactionId,
      locationUuid
    } = data
    const { rows } = await this.pgConnection.query(singleTransactionQuery, [transactionId, locationUuid])
    if (!rows[0]) {
      const error = new Error('Transaction not found')
      error.status = 404
      throw error
    }
    return rows[0]
  }

  async getTransactionType ({transactionId}) {
    const { rows } = await this.pgConnection.query(txnTypeQuery, [transactionId])
    if (!rows[0]) {
      const error = new Error('Transaction not found')
      error.status = 404
      throw error
    }
    return rows[0]
  }

  async getFutureInstallments ({locationId}) {
    const { rows } = await this.pgConnection.query(futureInstalmentQuery, [locationId])
    return {
      results: rows
    }
  }

  async getOrderCredits (orderId) {
    const {rows} = await this.pgConnection.query(getOrderCreditsQuery, [orderId])
    if (rows.length) return rows[0]
    return { amount: 0 }
  }

  async getCreditInfo ({ locationUuid, orderIdToExclude }) {
    const { rows: [location] } = await this.pgConnection.query(`
      with fs_location as (
        select
          fsid,
          uuid as location_id,
          orders_status,
          loan_limit
        from avocado.data_location where uuid = $1
      ),
      has_60_90_day_options as (
        select
          distinct po.location_id
        from fs_location l
        inner join avocado.payment_plan_options po on po.location_id = l.location_id
        where frequency in ('BIMONTHLY', 'TRIMONTHLY')
      )
      select
        l.*,
        case
          when coalesce(o.location_id, '') != '' then true
          else false
        end as has_60_day_option,
        case
          when coalesce(o.location_id, '') != '' then true
          else false
        end as has_90_day_option
      from fs_location l
      left join has_60_90_day_options o on l.location_id = o.location_id
    `, [locationUuid])

    if (!location) {
      const error = new Error('Location not found')
      error.status = 404
      throw error
    }

    const [
      pendingDeliveries,
      { results: payableTransactions },
      { results: allTransactions },
      arBalanceResponse = { ar_balance: 0 },
      lrBalanceResponse = { lr_balance: 0 },
      { status: creditApplicationStatus }
    ] = await Promise.all([
      this.dataShipment.getPendingDeliveries({
        locationId: locationUuid,
        weeks: SHIPMENT_WINDOW,
        priceAdapter: this.priceAdapter
      }),
      this.getPayablesList({ locationId: locationUuid, excludeOrderId: orderIdToExclude }),
      this.getList({ locationId: locationUuid, noLimit: true, overduePayment: null, startDate: null, endDate: null, txnType: null }),
      this.getAccountReceivableBalance({locationId: locationUuid}),
      this.getLoanReceivableBalance({locationId: locationUuid}),
      this.creditApplication.get(locationUuid)
    ])

    let overdueBalance
    const totalOutstandingAmount = payableTransactions.reduce((acc, transaction) => {
      if (transaction.week === 'overdue') {
        overdueBalance = transaction.totaldue
      }
      acc += transaction.totaldue
      return acc
    }, 0)

    const { pendingDeliveriesAmount, loanDeliveryAmountUsed } = pendingDeliveries
      .reduce((acc, pendingShipment) => {
        if (!pendingShipment.deliveryInvoiceAmount) {
          acc.loanDeliveryAmountUsed += pendingShipment.deliveryAmount
        }
        acc.pendingDeliveriesAmount += pendingShipment.deliveryAmount
        return acc
      }, { pendingDeliveriesAmount: 0, loanDeliveryAmountUsed: 0 })
    const { balance, pending } = calculateBalance({ transactions: allTransactions })

    return getCreditInfo({
      pendingDeliveriesAmount,
      arBalance: arBalanceResponse.ar_balance,
      lrBalance: lrBalanceResponse.lr_balance,
      totalOutstandingAmount,
      loanLimit: location.loan_limit,
      orderStatus: location.orders_status,
      creditApplicationStatus,
      balances: {
        balance,
        pending,
        overdue: overdueBalance
      },
      loanDeliveryAmountUsed,
      has60DayPaymentOption: location.has_60_day_option,
      has90DayPaymentOption: location.has_90_day_option
    })
  }
}

module.exports = TransactionsAdapter
